// A provider whose SDK never came up (the sidecar's ProviderInitError).
//
// The turn failed before any request left the machine, so the card must not
// read like a model's answer or a server's refusal. The sidecar used to hand
// this over as `UnknownError { message: "ProviderInitError" }`, and the card
// printed exactly that word — to a user whose GigaChat simply had no access key
// yet because the app was still registering with the gateway (a fresh install
// on a slow or filtered network: the install token is fetched in the
// background, with a 15 s first retry). The error now arrives as itself with a
// coarse `cause` (provider/init-error.ts in the sidecar), and each cause gets
// the sentence that names its fix.

export type ProviderInitCopy = {
  key:
    | "ui.sessionTurn.error.providerInit.credentials"
    | "ui.sessionTurn.error.providerInit.tls"
    | "ui.sessionTurn.error.providerInit.network"
    | "ui.sessionTurn.error.providerInit.other"
  params: { provider: string }
}

// How the provider is named to the user. The two we broker have product
// names; anything else keeps the id the user configured it under.
const PROVIDER_NAMES: Record<string, string> = {
  gigachat: "GigaChat",
  deepseek: "DeepSeek",
}

export function providerDisplayName(providerID: string): string {
  return PROVIDER_NAMES[providerID] ?? providerID
}

/**
 * The card copy for a provider-init failure; undefined for any other error.
 * `messageProviderID` is the failed message's own provider, used when the
 * error does not name one (it always should).
 */
export function providerInitCopy(error: unknown, messageProviderID = ""): ProviderInitCopy | undefined {
  if (!error || typeof error !== "object") return undefined
  const { name, data } = error as { name?: unknown; data?: { providerID?: unknown; cause?: unknown } }
  if (name !== "ProviderInitError") return undefined
  const providerID = typeof data?.providerID === "string" && data.providerID ? data.providerID : messageProviderID
  const params = { provider: providerDisplayName(providerID) }
  switch (data?.cause) {
    case "credentials":
      return { key: "ui.sessionTurn.error.providerInit.credentials", params }
    case "tls":
      return { key: "ui.sessionTurn.error.providerInit.tls", params }
    case "network":
      return { key: "ui.sessionTurn.error.providerInit.network", params }
    default:
      return { key: "ui.sessionTurn.error.providerInit.other", params }
  }
}
